/**
 * FolderTreeSidebar — collapsible SharePoint folder tree for the Document
 * Finder (Bundle Builder folder sidebar).
 *
 * Renders the firm drive's folder hierarchy from GET /api/documents/folders,
 * expanding lazily: a folder's children are fetched only the first time it is
 * expanded and cached per-folder-id thereafter, so re-expanding never re-hits
 * the network. Clicking a folder name asks the parent page to load that
 * folder's contents into the results list (via the shared /documents/find
 * folder_id path), so preview / multi-select / add-to-basket keep working on
 * folder-browsed documents exactly as they do on search hits.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ChevronRight, ChevronDown, Folder, FolderOpen, Loader2, AlertTriangle,
  PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { API } from "../../config/api";

async function fetchFolders(parentId) {
  const res = await axios.get(`${API}/documents/folders`, {
    params: parentId ? { parent_id: parentId } : {},
  });
  return res.data?.folders || [];
}

function FolderNode({ folder, depth, selectedFolderId, expanded, childrenCache, loadingId, onToggle, onSelect }) {
  const isExpanded = !!expanded[folder.id];
  const hasChildren = (folder.childCount || 0) > 0;
  const isSelected = selectedFolderId === folder.id;
  const kids = childrenCache[folder.id];
  const isLoading = loadingId === folder.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded-md pr-2 py-1 text-xs cursor-default ${
          isSelected ? "bg-teal/20 text-teal" : "text-slate-300 hover:bg-navy-hover"
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
        data-testid={`folder-node-${folder.id}`}
      >
        <button
          type="button"
          data-testid={`folder-toggle-${folder.id}`}
          onClick={() => hasChildren && onToggle(folder)}
          className={`shrink-0 p-0.5 rounded ${hasChildren ? "hover:bg-slate-700" : "invisible"}`}
          aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
          aria-expanded={isExpanded}
        >
          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <button
          type="button"
          data-testid={`folder-select-${folder.id}`}
          onClick={() => onSelect(folder)}
          className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
          title={folder.name}
        >
          {isExpanded ? (
            <FolderOpen size={14} className="shrink-0 text-teal" />
          ) : (
            <Folder size={14} className="shrink-0 text-slate-400" />
          )}
          <span className="truncate">{folder.name}</span>
        </button>
      </div>

      {isExpanded && (
        <div>
          {isLoading && (
            <div
              className="flex items-center gap-1 text-[11px] text-slate-500 py-1"
              style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
              data-testid={`folder-loading-${folder.id}`}
            >
              <Loader2 className="animate-spin" size={11} /> Loading…
            </div>
          )}
          {!isLoading && kids && kids.length === 0 && (
            <div
              className="text-[11px] text-slate-500 py-1"
              style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
              data-testid={`folder-empty-${folder.id}`}
            >
              No subfolders
            </div>
          )}
          {!isLoading && kids && kids.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              selectedFolderId={selectedFolderId}
              expanded={expanded}
              childrenCache={childrenCache}
              loadingId={loadingId}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTreeSidebar({ selectedFolderId, onSelectFolder }) {
  const [rootFolders, setRootFolders] = useState([]);
  const [expanded, setExpanded] = useState({});          // folderId -> bool
  const [childrenCache, setChildrenCache] = useState({}); // folderId -> folder[]
  const [loadingId, setLoadingId] = useState(null);
  const [rootLoading, setRootLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRootLoading(true);
    fetchFolders(null)
      .then((folders) => { if (!cancelled) setRootFolders(folders); })
      .catch(() => { if (!cancelled) setError("Couldn't load folders."); })
      .finally(() => { if (!cancelled) setRootLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleToggle = useCallback((folder) => {
    const willExpand = !expanded[folder.id];
    setExpanded((prev) => ({ ...prev, [folder.id]: willExpand }));
    // Lazy-fetch children the first time we expand; cache keeps re-expands free.
    if (willExpand && childrenCache[folder.id] === undefined) {
      setLoadingId(folder.id);
      fetchFolders(folder.id)
        .then((kids) => setChildrenCache((prev) => ({ ...prev, [folder.id]: kids })))
        .catch(() => setChildrenCache((prev) => ({ ...prev, [folder.id]: [] })))
        .finally(() => setLoadingId((cur) => (cur === folder.id ? null : cur)));
    }
  }, [expanded, childrenCache]);

  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="folder-tree-expand"
        onClick={() => setCollapsed(false)}
        className="shrink-0 h-9 px-2 flex items-center gap-1 rounded-md text-xs text-slate-300 bg-navy-card border border-navy-border hover:bg-navy-hover"
        title="Show folders"
      >
        <PanelLeftOpen size={15} /> Folders
      </button>
    );
  }

  return (
    <div
      className="w-full lg:w-64 shrink-0 rounded-lg border border-navy-border bg-navy-card p-2 lg:sticky lg:top-6 self-start"
      data-testid="folder-tree-sidebar"
    >
      <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-navy-border">
        <span className="text-xs font-semibold text-slate-300">Folders</span>
        <button
          type="button"
          data-testid="folder-tree-collapse"
          onClick={() => setCollapsed(true)}
          className="p-1 rounded text-slate-400 hover:text-white hover:bg-navy-hover"
          title="Hide folders"
          aria-label="Hide folders"
        >
          <PanelLeftClose size={15} />
        </button>
      </div>

      {rootLoading && (
        <div className="flex items-center gap-1 text-[11px] text-slate-500 py-2 px-1" data-testid="folder-tree-loading">
          <Loader2 className="animate-spin" size={12} /> Loading folders…
        </div>
      )}
      {!rootLoading && error && (
        <div className="flex items-center gap-1 text-[11px] text-amber-300 py-2 px-1" data-testid="folder-tree-error">
          <AlertTriangle size={12} /> {error}
        </div>
      )}
      {!rootLoading && !error && rootFolders.length === 0 && (
        <div className="text-[11px] text-slate-500 py-2 px-1" data-testid="folder-tree-empty">
          No folders available.
        </div>
      )}
      {!rootLoading && !error && rootFolders.length > 0 && (
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {rootFolders.map((folder) => (
            <FolderNode
              key={folder.id}
              folder={folder}
              depth={0}
              selectedFolderId={selectedFolderId}
              expanded={expanded}
              childrenCache={childrenCache}
              loadingId={loadingId}
              onToggle={handleToggle}
              onSelect={onSelectFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}
